import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Badge } from "@/components/ui/badge";
import { NewsItem } from "@/types/news";
import { useTheme } from "@/context/ThemeContext";
import { getNewsUITextLabel } from "@/utils/newsUtils";
import { getLanguage } from "@/utils/languageUtils";
import { formatRichTextToHtml } from "@/utils/formatRichText";
import { useNewsFullSizeImage } from "@/hooks/useNewsFullSizeImage";
import { Calendar, Clock, X, ZoomIn } from "lucide-react";
import { NativeImage } from "@/components/native/NativeImage";
import { ZoomableImage } from "@/components/native/ZoomableImage";
import { nativeMediaViewerZ } from "@/lib/native/nativeZIndex";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { nativeSheetScrollBottomPad } from "@/lib/native/nativeSheetPadding";
import { cn } from "@/lib/utils";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useNativeMediaViewerOpen } from "@/hooks/useNativeMediaViewerOpen";
import { useGallerySwipeHandlers } from "@/hooks/useGallerySwipeHandlers";
import { useMediaViewerGestureLock } from "@/lib/native/mediaViewerGestureLock";
import { useNativeSheetOpensExpanded } from "@/lib/native/useNativeSheetOpensExpanded";


interface NewsPopupProps {
  news: NewsItem | null;
  onClose: () => void;
}

function NewsArticleImage({
  displayImageUrl,
  alt,
  onImageClick,
  labels,
}: {
  displayImageUrl: string;
  alt: string;
  onImageClick?: () => void;
  labels?: NewsItem["labels"];
}) {
  const [loaded, setLoaded] = useState(false);
  const imageNode = (
    <>
      {!loaded && (
        <div
          className="absolute inset-0 animate-pulse bg-neutral-800"
          aria-hidden
        />
      )}
      <NativeImage
        src={displayImageUrl}
        alt={alt}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        className={cn(
          "w-full transition-opacity duration-200",
          onImageClick
            ? "max-h-[min(40vh,360px)] object-contain"
            : "h-full object-cover",
          loaded ? "opacity-100" : "opacity-0",
        )}
      />
    </>
  );

  const labelBadges =
    labels && labels.length > 0 ? (
      <div className="absolute top-4 left-4 flex flex-wrap gap-2">
        {labels.map((label, idx) => (
          <Badge
            key={idx}
            className="text-xs bg-blue-500/90 text-white border-0 backdrop-blur-sm"
          >
            {label}
          </Badge>
        ))}
      </div>
    ) : null;

  if (onImageClick) {
    return (
      <button
        type="button"
        className="relative w-full shrink-0 group cursor-zoom-in bg-neutral-900 min-h-[min(28vh,220px)]"
        onClick={onImageClick}
        aria-label={getNewsUITextLabel("viewImage")}
      >
        {imageNode}
        <div className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-1.5 text-xs text-white opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100">
          <ZoomIn className="h-3.5 w-3.5" />
          {getNewsUITextLabel("viewImage")}
        </div>
        {labelBadges}
      </button>
    );
  }

  return (
    <div className="relative w-full h-48 md:h-64 overflow-hidden shrink-0 bg-neutral-900">
      {imageNode}
      {labelBadges}
    </div>
  );
}

function NewsArticleBody({
  news,
  isDark,
  showInlineClose,
  onClose,
  imageUrl,
  onImageClick,
}: {
  news: NewsItem;
  isDark: boolean;
  showInlineClose?: boolean;
  onClose: () => void;
  imageUrl?: string;
  onImageClick?: () => void;
}) {
  const wordCount = news.content?.split(/\s+/).length || 0;
  const readingTime = Math.ceil(wordCount / 200);
  const displayImageUrl = imageUrl || news.image?.url;

  return (
    <>
      {showInlineClose && (
        <button
          type="button"
          className={`absolute top-4 right-4 z-20 p-2 rounded-full ${
            isDark
              ? "bg-black/50 text-white hover:bg-black/70"
              : "bg-white/90 text-gray-800 hover:bg-white"
          }`}
          onClick={onClose}
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      )}

      {news.image && displayImageUrl && (
        <NewsArticleImage
          displayImageUrl={displayImageUrl}
          alt={news.title}
          onImageClick={onImageClick}
          labels={news.labels}
        />
      )}

      <div
        className={cn(
          "p-5 md:p-6 overflow-y-auto flex-1 min-h-0",
          nativeSheetScrollBottomPad,
        )}
      >
        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-4 h-4" />
            <span>
              {new Date(news.createdAt).toLocaleDateString(
                getLanguage() === "en" ? "en-US" : "sv-SE",
                { year: "numeric", month: "long", day: "numeric" },
              )}
            </span>
          </div>
          {readingTime > 0 && (
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              <span>
                {readingTime} {getNewsUITextLabel("minReading")}
              </span>
            </div>
          )}
        </div>

        <h2
          className={`text-xl md:text-2xl font-bold mb-4 leading-tight ${
            showInlineClose ? "pr-10" : ""
          } ${isDark ? "text-white" : "text-black"}`}
        >
          {news.title}
        </h2>

        {!news.image && news.labels && news.labels.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2">
            {news.labels.map((label, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {label}
              </Badge>
            ))}
          </div>
        )}

        <div
          className={`text-xs md:text-sm ${
            isDark ? "text-gray-300" : "text-gray-700"
          }`}
          dangerouslySetInnerHTML={{
            __html: formatRichTextToHtml(news.content || ""),
          }}
        />
      </div>
    </>
  );
}

const NewsPopup = ({ news, onClose }: NewsPopupProps) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { isNative } = useNativePlatform();
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const imageUrl = useNewsFullSizeImage(news?.image?.id, news?.image?.url);
  const closeImagePreview = useCallback(() => setImagePreviewOpen(false), []);
  const imageGestureLockRef = useMediaViewerGestureLock();
  useNativeSheetOpensExpanded(!!news, isNative);
  useNativeMediaViewerOpen(imagePreviewOpen);
  const imagePreviewSwipeHandlers = useGallerySwipeHandlers({
    enabled: imagePreviewOpen,
    onDismiss: closeImagePreview,
    gestureLockRef: imageGestureLockRef,
  });

  useEffect(() => {
    if (isNative) return;

    if (news) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
      setImagePreviewOpen(false);
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [news, isNative]);

  if (!news) return null;

  const imagePreview =
    imagePreviewOpen && news.image && imageUrl ? (
      <div
        className={cn(
          "native-media-viewer-layer fixed inset-0 h-dvh w-dvh bg-black/95",
          isNative ? nativeMediaViewerZ : "z-[200]",
        )}
        role="dialog"
        aria-modal="true"
        aria-label={news.title}
      >
        <div
          className="relative flex h-full w-full items-center justify-center overflow-hidden"
          onTouchStart={(e) => imagePreviewSwipeHandlers.onTouchStart?.(e)}
          onTouchMove={(e) => imagePreviewSwipeHandlers.onTouchMove?.(e)}
          onTouchEnd={(e) => imagePreviewSwipeHandlers.onTouchEnd?.(e)}
        >
          <ZoomableImage
            src={imageUrl}
            alt={news.title}
            className="h-full w-full max-h-full max-w-full"
            imgClassName="max-h-[85vh] max-w-[min(96vw,1200px)]"
            draggable={false}
            gestureLockRef={imageGestureLockRef}
          />

          <button
            type="button"
            className={cn(
              "absolute right-4 z-[9999] rounded-full bg-black/50 p-2.5 text-white hover:bg-black/70 touch-manipulation",
              isNative
                ? "top-[calc(var(--native-safe-top,env(safe-area-inset-top,0px))+0.75rem)]"
                : "top-4",
            )}
            onClick={closeImagePreview}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    ) : null;

  if (isNative) {
    return (
      <>
        <Sheet
          open
          onOpenChange={(open) => {
            if (!open) {
              setImagePreviewOpen(false);
              onClose();
            }
          }}
        >
          <SheetContent
            side="bottom"
            expandable
            className="native-news-sheet native-sheet-expandable flex flex-col gap-0 p-0 overflow-hidden"
          >
            <NewsArticleBody
              news={news}
              isDark={isDark}
              onClose={onClose}
              imageUrl={imageUrl}
              onImageClick={() => setImagePreviewOpen(true)}
            />
          </SheetContent>
        </Sheet>
        {imagePreview && createPortal(imagePreview, document.body)}
      </>
    );
  }

  const popup = (
    <>
      <div
        className="fixed inset-0 bg-black/60 flex items-center justify-center z-[120] p-4 sm:p-5"
        onClick={onClose}
      >
        <div
          className={`rounded-lg shadow-2xl max-w-2xl w-full relative overflow-hidden flex flex-col max-h-[min(85vh,900px)] ${
            isDark ? "bg-black text-white" : "bg-white text-black"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <NewsArticleBody
            news={news}
            isDark={isDark}
            showInlineClose
            onClose={onClose}
            imageUrl={imageUrl}
            onImageClick={() => setImagePreviewOpen(true)}
          />
        </div>
      </div>
      {imagePreview}
    </>
  );

  return createPortal(popup, document.body);
};

export default NewsPopup;
